/**
 * 커스텀 국제 전화 입력 (외부 라이브러리 없음).
 * 인라인: [ 국가 버튼(플래그 + dial code) ] [ 번호 input ], 작은 드롭다운으로 국가 선택.
 * 컨트롤러에서는 new IntlPhoneInput(containerSelector, options).init() 만 사용.
 */

export interface CountryOption {
  iso2: string;
  dialCode: string;
  name: string;
}

/** 주요 국가 리스트 (일부 생략 가능, 필요 시 확장) */
const COUNTRIES: CountryOption[] = [
  { iso2: "kr", dialCode: "82", name: "South Korea" },
  { iso2: "us", dialCode: "1", name: "United States" },
  { iso2: "gb", dialCode: "44", name: "United Kingdom" },
  { iso2: "jp", dialCode: "81", name: "Japan" },
  { iso2: "cn", dialCode: "86", name: "China" },
  { iso2: "de", dialCode: "49", name: "Germany" },
  { iso2: "fr", dialCode: "33", name: "France" },
  { iso2: "au", dialCode: "61", name: "Australia" },
  { iso2: "ca", dialCode: "1", name: "Canada" },
  { iso2: "in", dialCode: "91", name: "India" },
  { iso2: "br", dialCode: "55", name: "Brazil" },
  { iso2: "mx", dialCode: "52", name: "Mexico" },
  { iso2: "it", dialCode: "39", name: "Italy" },
  { iso2: "es", dialCode: "34", name: "Spain" },
  { iso2: "nl", dialCode: "31", name: "Netherlands" },
  { iso2: "ru", dialCode: "7", name: "Russia" },
  { iso2: "sg", dialCode: "65", name: "Singapore" },
  { iso2: "hk", dialCode: "852", name: "Hong Kong" },
  { iso2: "tw", dialCode: "886", name: "Taiwan" },
  { iso2: "th", dialCode: "66", name: "Thailand" },
  { iso2: "vn", dialCode: "84", name: "Vietnam" },
  { iso2: "ph", dialCode: "63", name: "Philippines" },
  { iso2: "my", dialCode: "60", name: "Malaysia" },
  { iso2: "id", dialCode: "62", name: "Indonesia" },
  { iso2: "nz", dialCode: "64", name: "New Zealand" },
  { iso2: "pl", dialCode: "48", name: "Poland" },
  { iso2: "se", dialCode: "46", name: "Sweden" },
  { iso2: "no", dialCode: "47", name: "Norway" },
  { iso2: "ch", dialCode: "41", name: "Switzerland" },
  { iso2: "at", dialCode: "43", name: "Austria" },
  { iso2: "be", dialCode: "32", name: "Belgium" },
  { iso2: "ae", dialCode: "971", name: "United Arab Emirates" },
  { iso2: "sa", dialCode: "966", name: "Saudi Arabia" },
  { iso2: "il", dialCode: "972", name: "Israel" },
  { iso2: "tr", dialCode: "90", name: "Turkey" },
  { iso2: "za", dialCode: "27", name: "South Africa" },
  { iso2: "eg", dialCode: "20", name: "Egypt" },
  { iso2: "ng", dialCode: "234", name: "Nigeria" },
  { iso2: "af", dialCode: "93", name: "Afghanistan" },
  { iso2: "al", dialCode: "355", name: "Albania" },
  { iso2: "ar", dialCode: "54", name: "Argentina" },
  { iso2: "bd", dialCode: "880", name: "Bangladesh" },
  { iso2: "by", dialCode: "375", name: "Belarus" },
  { iso2: "cl", dialCode: "56", name: "Chile" },
  { iso2: "co", dialCode: "57", name: "Colombia" },
  { iso2: "cz", dialCode: "420", name: "Czech Republic" },
  { iso2: "dk", dialCode: "45", name: "Denmark" },
  { iso2: "fi", dialCode: "358", name: "Finland" },
  { iso2: "gr", dialCode: "30", name: "Greece" },
  { iso2: "hu", dialCode: "36", name: "Hungary" },
  { iso2: "ie", dialCode: "353", name: "Ireland" },
  { iso2: "pk", dialCode: "92", name: "Pakistan" },
  { iso2: "pt", dialCode: "351", name: "Portugal" },
  { iso2: "ro", dialCode: "40", name: "Romania" },
  { iso2: "ua", dialCode: "380", name: "Ukraine" },
];

function flagEmoji(iso2: string): string {
  const s = iso2.toUpperCase().slice(0, 2);
  if (s.length < 2) return "";
  return String.fromCodePoint(
    0x1f1e6 + s.charCodeAt(0) - 65,
    0x1f1e6 + s.charCodeAt(1) - 65
  );
}

export interface IntlPhoneInputOptions {
  initialCountry?: string;
  /** input name for the full number (e.g. "phone"). If not set, one input holds full value. */
  inputName?: string;
  /** container class for BEM (e.g. "intl-phone") */
  containerClass?: string;
}

const DEFAULT_CONTAINER_CLASS = "intl-phone";

export class IntlPhoneInput {
  private readonly root: HTMLElement;
  private readonly options: IntlPhoneInputOptions;
  private selected: CountryOption;
  private triggerEl: HTMLElement | null = null;
  private dropdownEl: HTMLElement | null = null;
  private inputEl: HTMLInputElement | null = null;
  private hiddenEl: HTMLInputElement | null = null;

  constructor(containerSelector: string, options: IntlPhoneInputOptions = {}) {
    const el = document.querySelector<HTMLElement>(containerSelector);
    if (!el) throw new Error("IntlPhoneInput: container not found");
    this.root = el;
    this.options = options;
    const initial = (options.initialCountry ?? "kr").toLowerCase();
    this.selected =
      COUNTRIES.find((c) => c.iso2 === initial) ?? COUNTRIES[0]!;
  }

  init(): void {
    const containerClass = this.options.containerClass ?? DEFAULT_CONTAINER_CLASS;
    this.root.classList.add(containerClass);

    const existingInput = this.root.querySelector<HTMLInputElement>("input[type=tel], input[name=phone]");
    const name = this.options.inputName ?? "phone";

    this.hiddenEl = document.createElement("input");
    this.hiddenEl.type = "hidden";
    this.hiddenEl.name = name;
    this.root.appendChild(this.hiddenEl);

    if (existingInput) {
      this.inputEl = existingInput;
      existingInput.removeAttribute("name");
      existingInput.setAttribute("data-intl-phone-national", "true");
    } else {
      this.inputEl = document.createElement("input");
      this.inputEl.type = "tel";
      this.inputEl.setAttribute("data-intl-phone-national", "true");
      this.inputEl.autocomplete = "tel-national";
      this.inputEl.placeholder = "";
    }

    const wrap = document.createElement("div");
    wrap.className = `${containerClass}__wrap`;

    this.triggerEl = document.createElement("button");
    this.triggerEl.type = "button";
    this.triggerEl.className = `${containerClass}__trigger`;
    this.triggerEl.setAttribute("aria-haspopup", "listbox");
    this.triggerEl.setAttribute("aria-expanded", "false");
    this.triggerEl.innerHTML = `<span class="${containerClass}__flag">${flagEmoji(this.selected.iso2)}</span><span class="${containerClass}__dial">+${this.selected.dialCode}</span>`;

    this.dropdownEl = document.createElement("div");
    this.dropdownEl.className = `${containerClass}__dropdown`;
    this.dropdownEl.setAttribute("role", "listbox");
    this.dropdownEl.hidden = true;
    const search = document.createElement("input");
    search.type = "search";
    search.className = `${containerClass}__search`;
    search.placeholder = "Search country";
    search.setAttribute("aria-label", "Search country");
    this.dropdownEl.appendChild(search);
    const list = document.createElement("div");
    list.className = `${containerClass}__list`;
    list.setAttribute("data-lenis-prevent", "");
    COUNTRIES.forEach((c) => {
      const item = document.createElement("div");
      item.className = `${containerClass}__item`;
      item.setAttribute("role", "option");
      item.dataset.iso2 = c.iso2;
      item.dataset.dialCode = c.dialCode;
      item.innerHTML = `<span class="${containerClass}__item-flag">${flagEmoji(c.iso2)}</span><span class="${containerClass}__item-name">${c.name}</span><span class="${containerClass}__item-dial">+${c.dialCode}</span>`;
      list.appendChild(item);
    });
    this.dropdownEl.appendChild(list);

    wrap.appendChild(this.triggerEl);
    wrap.appendChild(this.dropdownEl);
    wrap.appendChild(this.inputEl!);
    this.root.insertBefore(wrap, this.root.firstChild);

    this.triggerEl.addEventListener("click", (e) => {
      e.preventDefault();
      const open = this.dropdownEl?.hidden !== false;
      this.dropdownEl!.hidden = !open;
      this.triggerEl?.setAttribute("aria-expanded", String(!open));
      if (open) {
        (this.dropdownEl!.querySelector(`.${containerClass}__search`) as HTMLInputElement)?.focus();
        this.filterList("");
      }
    });

    search.addEventListener("input", () => this.filterList(search.value.trim()));
    search.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        this.dropdownEl!.hidden = true;
        this.triggerEl?.setAttribute("aria-expanded", "false");
      }
    });

    list.querySelectorAll(`.${containerClass}__item`).forEach((item) => {
      item.addEventListener("click", () => {
        const iso2 = (item as HTMLElement).dataset.iso2;
        const c = COUNTRIES.find((x) => x.iso2 === iso2);
        if (c) {
          this.selected = c;
          this.triggerEl!.innerHTML = `<span class="${containerClass}__flag">${flagEmoji(c.iso2)}</span><span class="${containerClass}__dial">+${c.dialCode}</span>`;
          this.dropdownEl!.hidden = true;
          this.triggerEl?.setAttribute("aria-expanded", "false");
          this.syncFullNumber();
        }
      });
    });

    document.addEventListener("click", (e) => {
      if (this.dropdownEl && !this.root.contains(e.target as Node)) {
        this.dropdownEl.hidden = true;
        this.triggerEl?.setAttribute("aria-expanded", "false");
      }
    });

    if (this.inputEl) {
      this.inputEl.addEventListener("input", () => this.syncFullNumber());
      this.inputEl.addEventListener("blur", () => this.syncFullNumber());
    }

    const initialVal = this.inputEl?.value ?? "";
    if (initialVal && initialVal.startsWith("+")) {
      const match = initialVal.match(/^\+(\d{1,4})(.*)$/);
      if (match) {
        const dial = match[1];
        const national = match[2].replace(/\D/g, "");
        const country = COUNTRIES.find((c) => c.dialCode === dial || initialVal.startsWith("+" + c.dialCode));
        if (country) {
          this.selected = country;
          this.triggerEl!.innerHTML = `<span class="${containerClass}__flag">${flagEmoji(country.iso2)}</span><span class="${containerClass}__dial">+${country.dialCode}</span>`;
          this.inputEl!.value = national;
        }
      }
    }
    this.syncFullNumber();
  }

  private filterList(query: string): void {
    const containerClass = this.options.containerClass ?? DEFAULT_CONTAINER_CLASS;
    const list = this.dropdownEl?.querySelector(`.${containerClass}__list`);
    if (!list) return;
    const q = query.toLowerCase();
    list.querySelectorAll(`.${containerClass}__item`).forEach((el) => {
      const item = el as HTMLElement;
      const name = (item.querySelector(`.${containerClass}__item-name`)?.textContent ?? "").toLowerCase();
      const dial = (item.dataset.dialCode ?? "").toLowerCase();
      const show = !q || name.includes(q) || dial.includes(q);
      (item as HTMLElement).hidden = !show;
    });
  }

  private syncFullNumber(): void {
    const national = (this.inputEl?.value ?? "").replace(/\D/g, "");
    const full = national ? `+${this.selected.dialCode}${national}` : "";
    if (this.hiddenEl) this.hiddenEl.value = full;
    if (this.inputEl && !this.hiddenEl) this.inputEl.value = full;
  }

  getNumber(): string {
    const national = (this.inputEl?.value ?? "").replace(/\D/g, "");
    return national ? `+${this.selected.dialCode}${national}` : "";
  }
}
